import React from "react";
import {useForm, Controller} from "react-hook-form";
import {Box, Stack} from "@chakra-ui/core";
import {produce} from "immer";
import FormControl from "@pency/components/form/FormControl";

import FieldInput, {validator as FieldInputValidator} from "../inputs/Field";

import {Field} from "~/tenant/types";

interface Props {
  defaultValues?: Field[];
  onSubmit: (values: Field[], formValues, total, getTotalWithDelivery) => void;
  total: number;
  getTotalWithDelivery: (formValues: any) => number;
  children: (options: {
    form: JSX.Element;
    formValues: FormData;
    isLoading: boolean;
    submit: (e?: React.BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>;
  }) => JSX.Element;
}

interface FormData {
  fields: string[];
}

const DELIVERY_METHOD_FIELD_ID = "default_delivery_mode";
const DELIVERY_ADDRESS_FIELD_ID = "default_delivery_address";
const HOME_DELIVERY_OPTION_ID = "home_delivery";

function getDefaultValue(field: Field) {
  return field.id === DELIVERY_METHOD_FIELD_ID
    ? undefined
    : field.value;
}

function getFieldValue(formValues, index: number) {
  if (index < 0) return undefined;

  return formValues?.fields?.[index] ?? formValues?.[`fields[${index}]`];
}

function isHomeDelivery(value: string | undefined) {
  return value === HOME_DELIVERY_OPTION_ID;
}

const FieldsForm: React.FC<Props> = ({defaultValues, children, getTotalWithDelivery, onSubmit, total}) => {
  const {handleSubmit: submit, watch, formState, control, errors} = useForm<FormData>({
    defaultValues: {fields: defaultValues?.map(getDefaultValue)},
  });
  const formValues = watch()

  const deliveryMethodIndex =
    defaultValues?.findIndex((field) => field.id === DELIVERY_METHOD_FIELD_ID) ?? -1;
  const deliveryMethodField = deliveryMethodIndex >= 0 ? defaultValues?.[deliveryMethodIndex] : undefined;
  // Only hide the shipping address when the delivery method field is active and
  // the customer did not choose home delivery.
  const showDeliveryAddress =
    !deliveryMethodField?.active ||
    isHomeDelivery(getFieldValue(formValues, deliveryMethodIndex));

  function isHiddenField(field: Field) {
    return field.id === DELIVERY_ADDRESS_FIELD_ID && !showDeliveryAddress;
  }

  function handleSubmit(values: FormData) {
    return onSubmit(
      produce(defaultValues || [], (fields) => {
        fields.forEach((field, index) => {
          field.value = isHiddenField(field)
            ? undefined
            : values.fields?.[index] ??
              (field.id === DELIVERY_METHOD_FIELD_ID ? undefined : field.value);
        });
      }),
      formValues, 
      total,
      getTotalWithDelivery
    );
  }

  return children({
    isLoading: formState.isSubmitting,
    submit: submit(handleSubmit),
    formValues: formValues,
    form: (
      <form onSubmit={submit(handleSubmit)}>
        <Stack spacing={8}>
          {defaultValues?.map((field, index) => {
            const error = errors.fields?.[index]?.message;

            if (!field.active) return null

            if (isHiddenField(field)) return null

            if (field.type === 'select' && field.id.includes("custom_field")) return  null

            // Wrap with a key per field: Stack's shouldWrapChildren keys by position, so showing
            // the address would remount the fields after it and reset their values.
            if (field.type === "note") return <Box key={field.id} display="inline-block"><FieldInput field={field} /></Box>;

            return (
              <Box key={field.id} display="inline-block">
              <FormControl
                error={error}
                isRequired={field.required}
                name={`fields[${index}]`}
              >
                <Controller
                  as={FieldInput}
                  control={control}
                  field={field}
                  name={`fields[${index}]`}
                  rules={{
                    validate: FieldInputValidator(field),
                  }}
                />
              </FormControl>
              </Box>
            );
          })}
        </Stack>
      </form>
    ),
  });
};

export default FieldsForm;
